import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import {
  CurationCheckCard,
  CurationVerdictPanel,
  cadenceError,
  cadencePayload,
  verifiedCell,
  verifiedLine,
  VERIFY_HELP,
  type CurationCheck,
  type VerificationFields,
  supersessionPillClass,
} from "../lib/curationCheck.js";
import { type ServingRow, servingStatus, perRowServingStatus } from "../lib/servingStatus.js";
import { USAGE_HELP, usageCountCell, usageProse, usageTimeCell, usageTimeSortValue, type EntryUsage } from "../lib/knowledgeUsage.js";
import { proposalOriginLabel, proposedByLabel } from "../lib/proposalOrigin.js";

// ─────────────────────────────────────────────
// Types (mirror the hub frontmatter shape)
// ─────────────────────────────────────────────

type Entry = {
  name: string;
  agent_type: string;
  category: string;
  priority: number;
  scope_tags: string[];
  status: string; // accepted | draft
  enabled: boolean;
  series_id: string;
  description: string;
  related: string[];
  body?: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
  /** ADR-0150 §3.1: absent = pre-classification (serves as always). */
  serving_class?: string;
  /** ADR-0151 §4.1: computed verification tier (absent while computing/on failure). */
  verification?: {
    tier: "guardrail" | "eval_deterministic" | "eval_advisory" | "unverified";
    annotations: string[];
    eval_cases: number;
    broken: boolean;
  };
  archive_reason?: string;
  archived_at?: string;
  /** ADR-0186 door retrieval counters; null = usage unavailable (renders as "—"). */
  usage?: EntryUsage | null;
  /** ADR-0201 — when a curator last confirmed this entry, and whether that lapsed. */
  verified_at?: string | null;
  verified_by_actor?: string | null;
  verify_every_days?: number | null;
  lapsed?: boolean;
};

type UsageSortKey = "search_hits" | "reads" | "last_hit" | "last_read";

function UsageSortableTH(props: {
  col: UsageSortKey;
  sortKey: UsageSortKey | null;
  sortDir: "asc" | "desc";
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  const active = props.sortKey === props.col;
  const arrow = active ? (props.sortDir === "asc" ? " ↑" : " ↓") : "";
  return (
    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={props.onClick} title={props.title}>
      {props.children}
      {arrow}
    </th>
  );
}

type Proposal = {
  id: string;
  name: string;
  agent_type: string;
  category: string;
  priority: number;
  scope_tags: string[];
  description: string;
  proposal_kind: string;
  origin?: string | null;
  /** Machine identifiers — rendered through `proposedByLabel`, never shown raw. */
  proposed_by?: string | null;
  updated_by?: string;
  session_owner?: string | null;
  supersedes?: string[] | null;
  supersedes_pending?: Array<{ id: string; name: string | null; present: boolean }>;
  superseded_by?: Array<{ id: string; name: string }>;
  rationale?: string;
  created_at: string;
  body: string;
  /** ADR-0150 §3.1 rule 4: producer-supplied class; the reviewer supplies it at accept when absent. */
  serving_class?: string | null;
  /** ADR-0201 §3.4 — the verdict this proposal was written with; null if never checked. */
  check?: CurationCheck | null;
  check_stale?: boolean;
};

// ADR-0150 §3.4 — the write-side serving gate's response shapes.
type ServingPressureRow = {
  surface_id: string;
  agent_type: string;
  budget_chars: number;
  served_chars: number;
  utilization: number;
  headroom_chars: number;
};
type ServingSummary = {
  refused: false;
  pressure: ServingPressureRow[];
  reclassify_suggestion: string | null;
};
type ServingRefusalDetail = {
  surfaces: {
    surface_id: string;
    agent_type: string;
    label: string;
    budget_chars: number;
    overflow_chars: number;
    context_dependent: boolean;
    dropped: { name: string; description: string }[];
  }[];
  remedy: string;
  agent_on_demand_capable: boolean;
};

type Meta = {
  agent_types: string[];
  categories: string[];
  scope_tags: { functional: string[]; capability: string[] };
};

type KeyRow = {
  id: string;
  name: string;
  token_prefix: string;
  scope: string;
  created_by_name?: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

type GateCase = { id: string; title: string; actual: unknown; expected: unknown; error?: string };

const SLUG_RE = /^[a-z0-9-]{3,64}$/;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function fmtDateTime(s: string | null | undefined): string {
  return s ? new Date(s).toLocaleString() : "—";
}
function agentPill(t: string) {
  return <span className="pill">{t.replace(/_/g, " ")}</span>;
}
function statusPill(e: { status: string; enabled: boolean }) {
  if (!e.enabled) return <span className="pill pillBad">disabled</span>;
  return <span className={`pill ${e.status === "accepted" ? "pillOk" : "pillWarn"}`}>{e.status}</span>;
}

// ADR-0151 §4.1 — the Verification pill. Color always paired with a text
// label; broken claims (enforcement cited but switched off) outrank everything.
function verificationPill(v: Entry["verification"]) {
  if (!v) return <span className="muted" style={{ fontSize: 12 }}>—</span>;
  if (v.broken) {
    return (
      <span className="pill pillBad" title="This entry cites an automatic check that is disabled or missing.">
        Broken claim
      </span>
    );
  }
  const title = [VERIFICATION_HELP[v.tier], ...v.annotations].filter(Boolean).join(" · ");
  if (v.tier === "guardrail") {
    return <span className="pill pillOk" title={title}>{v.annotations.length > 0 ? "Enforced · warn" : "Enforced"}</span>;
  }
  if (v.tier === "eval_deterministic") return <span className="pill" title={title}>Eval</span>;
  if (v.tier === "eval_advisory") return <span className="pill pillWarn" title={title}>Advisory</span>;
  return <span className="pill pillWarn" title={title}>Unverified</span>;
}

const VERIFICATION_HELP: Record<string, string> = {
  guardrail: "Checked automatically by code on every relevant output.",
  eval_deterministic: "Covered by deterministic eval cases run at every canon write.",
  eval_advisory: "Covered only by advisory (judge) eval cases — a weaker check.",
  unverified: "No automatic check and no eval case — unverified prose.",
};

// ADR-0150 §4.3: serving class is a value, not a status — text, no pill.
// Unclassified renders as the default it serves under, marked via title.
function servingClassCell(servingClass: string | null | undefined) {
  if (servingClass === "conditional") return <span style={{ fontSize: 12 }}>Conditional</span>;
  if (servingClass === "on_demand") return <span style={{ fontSize: 12 }}>On demand</span>;
  if (servingClass === "always") return <span style={{ fontSize: 12 }}>Always</span>;
  return (
    <span className="muted" style={{ fontSize: 12 }} title="Unclassified — serves as Always pending the classification pass.">
      Always ·
    </span>
  );
}

const SERVING_CLASS_HELP: Record<string, string> = {
  always: "Loaded into every session of this agent.",
  conditional: "Loaded when a session matches one of the entry's scope tags.",
  on_demand: "Listed in every prompt by name and description; the agent fetches the full text when the request calls for it.",
};


export function PlatformMarkdown({ body }: { body: string }) {
  return (
    <div className="knowledgeMarkdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main page: tabs
// ─────────────────────────────────────────────

type Tab = "entries" | "review" | "proposals" | "archive" | "keys";

export function AdminPlatformKnowledge() {
  const [tab, setTab] = useState<Tab>(() => {
    try {
      const v = localStorage.getItem("atlas.platformKnowledge.tab");
      return v === "review" || v === "proposals" || v === "archive" || v === "keys" ? (v as Tab) : "entries";
    } catch {
      return "entries";
    }
  });
  const [proposalCount, setProposalCount] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    try {
      localStorage.setItem("atlas.platformKnowledge.tab", tab);
    } catch {
      /* ignore */
    }
  }, [tab]);

  useEffect(() => {
    void (async () => {
      try {
        const [p, r] = await Promise.all([
          apiFetch<{ proposals: Proposal[] }>("/api/admin/platform-knowledge-proposals"),
          apiFetch<{ adjudications: Adjudication[] }>("/api/admin/canon-adjudications?status=awaiting_owner"),
        ]);
        setProposalCount(p.proposals.length);
        setReviewCount(r.adjudications.length);
      } catch {
        /* ignore */
      }
    })();
  }, [tab]);

  const tabBar = (
    <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: 12 }}>
      {([
        ["entries", "Entries"],
        ["review", reviewCount > 0 ? `Review (${reviewCount >= 99 ? "99+" : reviewCount})` : "Review"],
        ["proposals", proposalCount > 0 ? `Proposals (${proposalCount >= 99 ? "99+" : proposalCount})` : "Proposals"],
        ["archive", "Archive"],
        ["keys", "Keys"],
      ] as [Tab, string][]).map(([t, label]) => (
        <button
          key={t}
          className={`btn${tab === t ? " btnPrimary" : ""}`}
          style={{ fontSize: 13, padding: "6px 14px", borderRadius: "6px 6px 0 0" }}
          onClick={() => setTab(t)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <PageLayout title="Platform Knowledge">
      {tabBar}
      {tab === "entries" && <EntriesTab />}
      {tab === "review" && <ReviewTab onChange={() => setReviewCount((c) => Math.max(0, c - 1))} />}
      {tab === "proposals" && <ProposalsTab onChange={() => setTab("proposals")} />}
      {tab === "archive" && <ArchiveTab />}
      {tab === "keys" && <KeysTab />}
    </PageLayout>
  );
}

// ─────────────────────────────────────────────
// Entries tab
// ─────────────────────────────────────────────

// ADR-0149 §4.1 — the serving meter. One compact table, one row per serving
// surface, worst-first, so the budget cliff is the first thing read. Clicking a
// row drives the existing agent filter (one path per action).
type VerificationFamilySummary = Record<
  string,
  { guardrail: number; eval_deterministic: number; eval_advisory: number; unverified: number; broken: string[] }
>;

function ServingPanel({
  rows,
  verification,
  onPick,
}: {
  rows: ServingRow[];
  verification: VerificationFamilySummary | null;
  onPick: (agentType: string) => void;
}) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => servingStatus(a).rank - servingStatus(b).rank),
    [rows]
  );
  // ADR-0151 §4.3: one line per family with a non-trivial verification mix;
  // fully-enforced families stay quiet (the common case reads as silence).
  const verificationLine = useMemo(() => {
    if (!verification) return null;
    const parts: string[] = [];
    const broken: string[] = [];
    for (const [agent, c] of Object.entries(verification)) {
      broken.push(...c.broken);
      const bits: string[] = [];
      if (c.guardrail) bits.push(`${c.guardrail} enforced`);
      if (c.eval_deterministic) bits.push(`${c.eval_deterministic} eval`);
      if (c.eval_advisory) bits.push(`${c.eval_advisory} advisory`);
      if (c.unverified) bits.push(`${c.unverified} unverified`);
      if (c.eval_advisory || c.unverified) parts.push(`${agent.replace(/_/g, " ")}: ${bits.join(" · ")}`);
    }
    return { parts, broken };
  }, [verification]);
  return (
    <div className="card">
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Prompt serving</div>
      <div className="tableWrap">
        <table className="table">
          <thead>
            <tr><th>Surface</th><th>Budget</th><th>Served</th><th>On demand</th><th>Dropped</th><th>Status</th></tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const st = servingStatus(r);
              const droppedChars = r.dropped.reduce((s, d) => s + d.chars, 0);
              return (
                <tr
                  key={`${r.surface_id}:${r.agent_type}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => onPick(r.agent_type)}
                >
                  <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                    {r.agent_type.replace(/_/g, " ")}
                    {r.surface_id !== r.agent_type && (
                      <span className="muted"> · {r.surface_id.replace(/_/g, " ")}</span>
                    )}
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>{r.budget_chars.toLocaleString()}</td>
                  <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                    {r.served_entries} of {r.selected_entries} · {r.served_chars.toLocaleString()} chars
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                    {r.on_demand_entries ? `${r.on_demand_entries} · ${r.index_chars.toLocaleString()}c index` : "—"}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {r.dropped.length ? `${r.dropped.length} · ${droppedChars.toLocaleString()} chars` : "—"}
                  </td>
                  <td><span className={`pill ${st.cls}`}>{st.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {verificationLine && verificationLine.broken.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--danger-text)" }}>
          Broken verification claims (a cited automatic check is disabled): {verificationLine.broken.join(", ")}
        </div>
      )}
      {verificationLine && verificationLine.parts.length > 0 && (
        <div className="muted" style={{ marginTop: verificationLine.broken.length > 0 ? 4 : 8, fontSize: 12 }}>
          Verification gaps — {verificationLine.parts.join(" · ")}
        </div>
      )}
    </div>
  );
}

function EntriesTab() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState("");
  const [q, setQ] = useState("");
  const [serving, setServing] = useState<ServingRow[] | null>(null);
  const [servingVerification, setServingVerification] = useState<VerificationFamilySummary | null>(null);
  const [servingError, setServingError] = useState<string | null>(null);
  // ADR-0186: usage columns are toggle-gated — this table already runs 10-11
  // columns and the identity column is sacred (UI_GUIDELINES §2.2).
  const [showUsage, setShowUsage] = useState<boolean>(() => {
    try { return localStorage.getItem("atlas.platformKnowledge.showUsage") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("atlas.platformKnowledge.showUsage", showUsage ? "1" : "0"); } catch { /* ignore */ }
  }, [showUsage]);
  const [usageSortKey, setUsageSortKey] = useState<UsageSortKey | null>(null);
  const [usageSortDir, setUsageSortDir] = useState<"asc" | "desc">("desc");
  const toggleUsageSort = (col: UsageSortKey) => {
    if (usageSortKey !== col) {
      setUsageSortKey(col);
      setUsageSortDir("desc");
    } else {
      setUsageSortDir((d) => (d === "desc" ? "asc" : "desc"));
    }
  };
  const sortedEntries = useMemo(() => {
    // Sort applies only while the usage columns are visible.
    if (!showUsage || !usageSortKey) return entries;
    const dir = usageSortDir === "asc" ? 1 : -1;
    const value = (e: Entry): number => {
      switch (usageSortKey) {
        case "search_hits": return e.usage ? e.usage.served_in_search : -Infinity;
        case "reads": return e.usage ? e.usage.read_count : -Infinity;
        case "last_hit": return usageTimeSortValue(e.usage?.last_served_at);
        case "last_read": return usageTimeSortValue(e.usage?.last_read_at);
      }
    };
    return [...entries].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va !== vb) return (va < vb ? -1 : 1) * dir;
      return a.name.localeCompare(b.name);
    });
  }, [entries, showUsage, usageSortKey, usageSortDir]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (agentFilter) params.set("agent_type", agentFilter);
      if (q.trim()) params.set("q", q.trim());
      const qs = params.toString();
      const [res, m] = await Promise.all([
        apiFetch<{ entries: Entry[] }>(`/api/admin/platform-knowledge${qs ? `?${qs}` : ""}`),
        meta ? Promise.resolve({ ...meta } as unknown as Meta) : apiFetch<Meta>("/api/admin/platform-knowledge-meta"),
      ]);
      setEntries(res.entries);
      if (!meta) setMeta(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentFilter, q]);

  useEffect(() => {
    void load();
  }, [load]);

  // Serving accounting is diagnostic and independent — a failure here must never
  // block curation (§4.3), so it has its own state and is fetched once on mount.
  useEffect(() => {
    let alive = true;
    apiFetch<{ serving: ServingRow[]; verification?: VerificationFamilySummary | null }>("/api/admin/platform-knowledge-serving")
      .then((r) => { if (alive) { setServing(r.serving); setServingVerification(r.verification ?? null); } })
      .catch((e) => { if (alive) setServingError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, []);

  // Per-row serving status for the active agent filter: unreachable (never
  // selected) takes precedence over dropped (selected but over budget). Served
  // rows get no marker — the common case stays quiet (§1.1, §4.2).
  const perRow = useMemo(
    () => perRowServingStatus(serving ?? [], agentFilter),
    [serving, agentFilter]
  );

  if (error) return <ErrorState detail={error} />;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="toolbar">
        <div className="toolbarGroup">
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <span className="label" style={{ fontSize: 12 }}>Agent:</span>
            <select className="select" style={{ fontSize: 12 }} value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}>
              <option value="">All</option>
              {(meta?.agent_types ?? []).map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
          </label>
          <input
            className="input"
            style={{ fontSize: 12, width: 220 }}
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }} title={USAGE_HELP.caption}>
            <input type="checkbox" checked={showUsage} onChange={(e) => setShowUsage(e.target.checked)} />
            <span className="label" style={{ fontSize: 12 }}>Show usage</span>
          </label>
        </div>
        <div className="toolbarGroup">
          <button className="btn btnPrimary" onClick={() => navigate("/admin/platform-knowledge/new")}>+ New Entry</button>
        </div>
      </div>
      {servingError ? (
        <ErrorState detail={servingError} />
      ) : serving ? (
        <ServingPanel rows={serving} verification={servingVerification} onPick={setAgentFilter} />
      ) : (
        <LoadingState />
      )}
      {loading ? (
        <LoadingState />
      ) : (
        <>
        {showUsage && <div className="muted" style={{ fontSize: 12 }}>{USAGE_HELP.caption}</div>}
        <div className="tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Agent</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Serving</th>
                <th>Verification</th>
                <th>Status</th>
                <th>Updated</th>
                <th title={VERIFY_HELP.column}>Verified</th>
                <th>By</th>
                {agentFilter && <th>Serving status</th>}
                {showUsage && (
                  <>
                    <UsageSortableTH col="search_hits" sortKey={usageSortKey} sortDir={usageSortDir} onClick={() => toggleUsageSort("search_hits")} title={USAGE_HELP.searchHits}>Search hits</UsageSortableTH>
                    <UsageSortableTH col="reads" sortKey={usageSortKey} sortDir={usageSortDir} onClick={() => toggleUsageSort("reads")} title={USAGE_HELP.reads}>Reads</UsageSortableTH>
                    <UsageSortableTH col="last_hit" sortKey={usageSortKey} sortDir={usageSortDir} onClick={() => toggleUsageSort("last_hit")} title={USAGE_HELP.lastHit}>Last hit</UsageSortableTH>
                    <UsageSortableTH col="last_read" sortKey={usageSortKey} sortDir={usageSortDir} onClick={() => toggleUsageSort("last_read")} title={USAGE_HELP.lastRead}>Last read</UsageSortableTH>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={11 + (agentFilter ? 1 : 0) + (showUsage ? 4 : 0)} className="muted" style={{ textAlign: "center", padding: 24 }}>No entries.</td></tr>
              )}
              {sortedEntries.map((e) => {
                const serve = agentFilter ? perRow.get(e.name) : undefined;
                return (
                <tr key={e.name} style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/platform-knowledge/entry/${e.name}`)}>
                  <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{e.name}</td>
                  <td className="muted" style={{ fontSize: 12, maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.description}>{e.description}</td>
                  <td>{agentPill(e.agent_type)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{e.category}</td>
                  <td className="mono" style={{ fontSize: 12 }}>{e.priority}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{servingClassCell(e.serving_class)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{verificationPill(e.verification)}</td>
                  <td>{statusPill(e)}</td>
                  <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtDateTime(e.updated_at)}</td>
                  {verifiedCell(e)}
                  <td className="muted" style={{ fontSize: 12 }}>{e.updated_by.replace(/^(account|key|capture|escalation|system):/, "")}</td>
                  {agentFilter && (
                    <td>{serve ? <span className={`pill ${serve.cls}`} title={serve.title}>{serve.label}</span> : null}</td>
                  )}
                  {showUsage && (
                    <>
                      {usageCountCell(e.usage, e.usage?.served_in_search)}
                      {usageCountCell(e.usage, e.usage?.read_count)}
                      {usageTimeCell(e.usage, e.usage?.last_served_at)}
                      {usageTimeCell(e.usage, e.usage?.last_read_at)}
                    </>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Review tab (ADR-0167) — measured canon writes awaiting the owner's click
// ─────────────────────────────────────────────

// A measured canon write. Writes land live immediately; the eval suite runs
// async and anything not fully clean waits here for keep/revert.
type Adjudication = {
  id: string;
  tenant_id: string | null;
  agent_type: string;
  entry_name: string | null;
  source: "canon_write" | "legacy_pattern_change";
  run_id: string | null;
  verdict: "clean" | "blocking_failures" | "regression" | "error";
  delta: {
    score: number;
    total: number;
    baseline_score: number | null;
    regressions: { id: string; title: string }[];
    blocking: { id: string; title: string; detail?: string }[];
    advisory: { id: string; title: string }[];
    unearned?: { id: string; title: string }[];
    error?: string;
  };
  status: "auto_cleared" | "awaiting_owner" | "kept" | "reverted";
  revertable: boolean;
  created_at: string;
  resolved_at: string | null;
};

const VERDICT_LABELS: Record<Adjudication["verdict"], { label: string; cls: string }> = {
  clean: { label: "Clean", cls: "pillOk" },
  blocking_failures: { label: "Failing checks", cls: "pillWarn" },
  regression: { label: "Regression", cls: "pillBad" },
  error: { label: "Measurement error", cls: "pillBad" },
};

const ADJ_STATUS_LABELS: Record<Adjudication["status"], string> = {
  auto_cleared: "landed clean",
  awaiting_owner: "awaiting review",
  kept: "kept",
  reverted: "reverted",
};

function adjScoreLine(a: Adjudication): string {
  const base = a.delta.baseline_score;
  const now = `${a.delta.score}/${a.delta.total}`;
  if (base === null) return `Suite score ${now} (first measured run for this family)`;
  const dir = a.delta.score > base ? "up from" : a.delta.score < base ? "down from" : "same as";
  return `Suite score ${now} — ${dir} ${base}/${a.delta.total} on the previous run`;
}

function ReviewTab({ onChange }: { onChange: () => void }) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [awaiting, setAwaiting] = useState<Adjudication[]>([]);
  const [recent, setRecent] = useState<Adjudication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, r] = await Promise.all([
        apiFetch<{ adjudications: Adjudication[] }>("/api/admin/canon-adjudications?status=awaiting_owner"),
        apiFetch<{ adjudications: Adjudication[] }>("/api/admin/canon-adjudications?limit=25"),
      ]);
      setAwaiting(a.adjudications);
      setRecent(r.adjudications.filter((x) => x.status !== "awaiting_owner"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function resolve(a: Adjudication, action: "keep" | "revert") {
    if (action === "revert") {
      const ok = await confirm({
        title: `Revert “${a.entry_name ?? "this change"}”?`,
        detail: "Restores the entry to its content before this write. The write being reverted stays in the ledger.",
        confirmLabel: "Revert",
      });
      if (!ok) return;
    }
    setBusy(a.id);
    try {
      await apiFetch(`/api/admin/canon-adjudications/${a.id}/resolve`, { method: "POST", body: { action } });
      toast.push({ kind: "success", message: action === "keep" ? "Kept — the change stands." : "Reverted." });
      await load();
      onChange();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "stale_revert") {
        toast.push({ kind: "error", message: "The entry changed again after this measurement — review its current state instead." });
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : "Failed" });
      }
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (error) return <p className="errorText">{error}</p>;

  return (
    <div>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Knowledge changes land immediately and are measured by the eval suite in the background. Fully clean
        verdicts clear themselves; anything else waits here. <strong>Keep</strong> accepts the change as-is;{" "}
        <strong>Revert</strong> restores the entry to its content before the write.
      </p>

      {awaiting.length === 0 && <p className="muted">Nothing awaiting review.</p>}

      {awaiting.map((a) => {
        const v = VERDICT_LABELS[a.verdict];
        return (
          <div key={a.id} className="card" style={{ marginBottom: 10, padding: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong>{a.entry_name ?? "Pattern change"}</strong>
              <span className={`pill ${v.cls}`}>{v.label}</span>
              <span className="muted" style={{ fontSize: 12 }}>
                {a.agent_type} · {new Date(a.created_at).toLocaleString()}
              </span>
              <span style={{ flex: 1 }} />
              <button className="btn" disabled={busy === a.id} onClick={() => void resolve(a, "keep")}>
                Keep
              </button>
              {a.revertable && (
                <button className="btn btnDanger" disabled={busy === a.id} onClick={() => void resolve(a, "revert")}>
                  Revert
                </button>
              )}
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>{adjScoreLine(a)}</div>
            {a.delta.error && (
              <div className="errorText" style={{ fontSize: 13, marginTop: 4 }}>
                The measurement itself failed: {a.delta.error}
              </div>
            )}
            {a.delta.regressions.length > 0 && (
              <div style={{ fontSize: 13, marginTop: 4 }}>
                <strong>Regressed</strong> (passed before this change, failing now):
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {a.delta.regressions.map((c) => (
                    <li key={c.id}>{c.title}</li>
                  ))}
                </ul>
              </div>
            )}
            {a.delta.blocking.filter((c) => !a.delta.regressions.some((rg) => rg.id === c.id)).length > 0 && (
              <div style={{ fontSize: 13, marginTop: 4 }}>
                <strong>Failing</strong> (also failing before this change):
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {a.delta.blocking
                    .filter((c) => !a.delta.regressions.some((rg) => rg.id === c.id))
                    .map((c) => (
                      <li key={c.id}>{c.title}</li>
                    ))}
                </ul>
              </div>
            )}
            {a.delta.advisory.length > 0 && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Advisory (never blocking): {a.delta.advisory.map((c) => c.title).join("; ")}
              </div>
            )}
            {(a.delta.unearned?.length ?? 0) > 0 && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Failing but not stability-proven (advisory until 3 consecutive clean runs):{" "}
                {a.delta.unearned!.map((c) => c.title).join("; ")}
              </div>
            )}
          </div>
        );
      })}

      {recent.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, marginTop: 18 }}>Recent verdicts</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Entry</th>
                <th>Family</th>
                <th>Verdict</th>
                <th>Outcome</th>
                <th>Score</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((a) => (
                <tr key={a.id}>
                  <td>{a.entry_name ?? "—"}</td>
                  <td>{a.agent_type}</td>
                  <td>
                    <span className={`pill ${VERDICT_LABELS[a.verdict].cls}`}>{VERDICT_LABELS[a.verdict].label}</span>
                  </td>
                  <td>{ADJ_STATUS_LABELS[a.status]}</td>
                  <td>
                    {a.delta.score}/{a.delta.total}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{new Date(a.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Proposals tab (grouped by origin)
// ─────────────────────────────────────────────

function ProposalsTab({ onChange }: { onChange: () => void }) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // ADR-0150 §4.4: the accept-time serving class, per proposal — prefilled when
  // the proposal carries one, "Choose…" (empty) when it doesn't.
  const [acceptClass, setAcceptClass] = useState<Record<string, string>>({});
  const [tenantNameById, setTenantNameById] = useState<Map<string, string>>(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, tenantsRes] = await Promise.all([
        apiFetch<{ proposals: Proposal[] }>("/api/admin/platform-knowledge-proposals"),
        // Name lookup for promotion origins; on failure the header just omits
        // the tenant rather than falling back to the raw id.
        apiFetch<{ tenants: { id: string; name: string }[] }>("/api/admin/tenants").catch(() => null),
      ]);
      setProposals(res.proposals);
      if (tenantsRes) setTenantNameById(new Map(tenantsRes.tenants.map((t) => [t.id.toLowerCase(), t.name])));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** ADR-0201 §3.4 — re-run a proposal's check against canon as it is now. */
  async function recheck(id: string) {
    setBusy(id);
    try {
      const res = await apiFetch<{ proposal: Proposal }>(`/api/admin/platform-knowledge-proposals/${id}/recheck`, {
        method: "POST",
      });
      setProposals((rows) => rows.map((r) => (r.id === id ? { ...r, check: res.proposal.check ?? null } : r)));
      toast.push({ kind: "success", message: "Checked again." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Check failed" });
    } finally {
      setBusy(null);
    }
  }

  async function resolve(id: string, action: "accept" | "reject", servingClass?: string) {
    if (action === "reject") {
      const ok = await confirm({ title: "Reject proposal?", detail: "Discard this proposed entry.", confirmLabel: "Reject" });
      if (!ok) return;
    }
    setBusy(id);
    try {
      await apiFetch(`/api/admin/platform-knowledge-proposals/${id}/resolve`, {
        method: "POST",
        body: { action, ...(servingClass ? { serving_class: servingClass } : {}) },
      });
      toast.push({ kind: "success", message: action === "accept" ? "Accepted to canon." : "Rejected." });
      await load();
      onChange();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "eval_regression") {
        toast.push({ kind: "error", message: "The eval gate blocked this — edit it first, then accept." });
      } else if (e instanceof ApiRequestError && e.code === "serving_regression") {
        toast.push({ kind: "error", message: "Accepting this would drop guidance from live prompts — reclassify or tighten it first." });
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : "Failed" });
      }
    } finally {
      setBusy(null);
    }
  }

  const grouped = useMemo(() => {
    const m = new Map<string, Proposal[]>();
    for (const p of proposals) {
      const key = p.origin ?? "";
      (m.get(key) ?? m.set(key, []).get(key)!).push(p);
    }
    return [...m.entries()];
  }, [proposals]);

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;
  if (proposals.length === 0) return <div className="card"><div className="muted">No pending proposals.</div></div>;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {grouped.map(([origin, ps]) => (
        <div key={origin} className="card" style={{ display: "grid", gap: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {proposalOriginLabel(origin, tenantNameById)} <span className="muted" style={{ fontWeight: 400 }}>· {ps.length} proposal{ps.length === 1 ? "" : "s"}</span>
          </div>
          {ps.map((p) => {
            // §4.4: the class riding the accept — prefilled from the proposal;
            // Accept stays disabled until chosen when the proposal lacks one.
            const chosenClass = acceptClass[p.id] ?? p.serving_class ?? "";
            return (
            <div key={p.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 8, display: "grid", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <div>
                  <span className="mono" style={{ fontSize: 12 }}>{p.name}</span> {agentPill(p.agent_type)}{" "}
                  {(p.superseded_by?.length ?? 0) > 0 ? (
                    <span className={supersessionPillClass("superseded")}>superseded</span>
                  ) : (p.supersedes_pending ?? []).some((x) => x.present) ? (
                    <span className={supersessionPillClass("supersedes")}>supersedes</span>
                  ) : null}{" "}
                  <span className="muted" style={{ fontSize: 12 }}>{p.category} · priority {p.priority}</span>
                  <div className="muted" style={{ fontSize: 11 }}>
                    Proposed by {proposedByLabel(p.proposed_by, p.updated_by, p.session_owner)}
                  </div>
                </div>
                <div className="actions">
                  <select
                    className="select"
                    style={{ fontSize: 12 }}
                    value={chosenClass}
                    title="Serving class the entry lands with"
                    onChange={(e) => setAcceptClass((m) => ({ ...m, [p.id]: e.target.value }))}
                  >
                    <option value="" disabled>Choose serving…</option>
                    <option value="always">Always loaded</option>
                    <option value="conditional">Conditional (scope tags)</option>
                    <option value="on_demand">On demand</option>
                  </select>
                  <button className="btn" disabled={busy === p.id} onClick={() => void resolve(p.id, "reject")}>Reject</button>
                  <button className="btn btnPrimary" disabled={busy === p.id || !chosenClass} onClick={() => void resolve(p.id, "accept", chosenClass)}>
                    {busy === p.id ? "…" : "Accept"}
                  </button>
                </div>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>{p.description}</div>
              {p.rationale && <div className="help">Rationale: {p.rationale}</div>}
              {(p.supersedes_pending ?? []).some((x) => x.present) && (
                <div className="help">
                  Replaces {(p.supersedes_pending ?? []).filter((x) => x.present).map((x) => x.name).join(", ")} —
                  accepting this rejects {(p.supersedes_pending ?? []).filter((x) => x.present).length === 1 ? "it" : "them"}.
                </div>
              )}
              {(p.superseded_by?.length ?? 0) > 0 && (
                <div className="help">
                  Replaced by {(p.superseded_by ?? []).map((x) => x.name).join(", ")} — resolve that one instead.
                </div>
              )}
              {/* ADR-0201 §3.4 — the review opens with the verdict already in hand. */}
              <CurationVerdictPanel
                check={p.check ?? null}
                surface="platform"
                stale={p.check_stale === true}
                onRecheck={() => void recheck(p.id)}
                recheckBusy={busy === p.id}
              />
              <details>
                <summary className="muted" style={{ fontSize: 12, cursor: "pointer" }}>Preview body</summary>
                <div className="knowledgeMarkdownPane" style={{ marginTop: 6 }}><PlatformMarkdown body={p.body} /></div>
              </details>
            </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// Archive tab
// ─────────────────────────────────────────────

function ArchiveTab() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ entries: Entry[] }>("/api/admin/platform-knowledge-archived");
      setEntries(res.entries);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function restore(name: string) {
    setBusy(name);
    try {
      await apiFetch(`/api/admin/platform-knowledge/${name}/restore`, { method: "POST" });
      toast.push({ kind: "success", message: "Restored." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Restore failed" });
    } finally {
      setBusy(null);
    }
  }

  async function purge(name: string) {
    const ok = await confirm({
      title: "Purge archived entry?",
      detail: `Permanently delete "${name}" from the archive. This cannot be undone from the console.`,
      confirmLabel: "Purge",
    });
    if (!ok) return;
    setBusy(name);
    try {
      await apiFetch(`/api/admin/platform-knowledge/${name}/purge`, { method: "POST" });
      toast.push({ kind: "success", message: "Purged." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Purge failed" });
    } finally {
      setBusy(null);
    }
  }

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;

  return (
    <div className="tableWrap">
      <table className="table">
        <thead>
          <tr><th>Name</th><th>Description</th><th>Agent</th><th>Reason</th><th>Archived</th><th /></tr>
        </thead>
        <tbody>
          {entries.length === 0 && <tr><td colSpan={6} className="muted" style={{ textAlign: "center", padding: 24 }}>No archived entries.</td></tr>}
          {entries.map((e) => (
            <tr key={e.name}>
              <td className="mono" style={{ fontSize: 12 }}>{e.name}</td>
              <td className="muted" style={{ fontSize: 12, maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.description}>{e.description}</td>
              <td>{agentPill(e.agent_type)}</td>
              <td className="muted" style={{ fontSize: 12 }} title={e.archive_reason || ""}>{e.archive_reason || "—"}</td>
              <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtDateTime(e.archived_at)}</td>
              <td className="actions">
                <button className="adminListAction" disabled={busy === e.name} onClick={() => void restore(e.name)}>Restore</button>
                <button className="adminListAction isDanger" disabled={busy === e.name} onClick={() => void purge(e.name)}>Purge</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// Keys tab
// ─────────────────────────────────────────────

function KeysTab() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [keys, setKeys] = useState<KeyRow[]>([]);
  const [scopes, setScopes] = useState<{ value: string; label: string; description: string }[]>([]);
  const [mcpUrl, setMcpUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("platform_knowledge:read_write");
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<{ token: string; name: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ keys: KeyRow[]; available_scopes: { value: string; label: string; description: string }[]; mcp_url: string }>(
        "/api/admin/platform-knowledge-keys"
      );
      setKeys(res.keys);
      setScopes(res.available_scopes);
      setMcpUrl(res.mcp_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function mint() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await apiFetch<{ key: KeyRow; token: string }>("/api/admin/platform-knowledge-keys", {
        method: "POST",
        body: { name: name.trim(), scope },
      });
      setMinted({ token: res.token, name: res.key.name });
      setName("");
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Mint failed" });
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k: KeyRow) {
    const ok = await confirm({
      title: "Revoke key?",
      detail: `Revoke "${k.name}"? Any live session using it is terminated immediately.`,
      confirmLabel: "Revoke",
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/admin/platform-knowledge-keys/${k.id}/revoke`, { method: "POST" });
      toast.push({ kind: "success", message: "Revoked." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Revoke failed" });
    }
  }

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 600 }}>Mint a curation key</div>
        <div className="help">
          A key lets an AI tool curate this guidance over the Atlas MCP endpoint. Treat it like a platform-admin credential —
          it shapes every tenant's agents. It is shown once.
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
          <label className="field" style={{ flex: "1 1 220px" }}>
            <span className="label">Name</span>
            <input className="input" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. claude-code" />
          </label>
          <label className="field" style={{ flex: "1 1 220px" }}>
            <span className="label">Scope</span>
            <select className="select" value={scope} onChange={(e) => setScope(e.target.value)}>
              {scopes.map((s) => (
                <option key={s.value} value={s.value}>{s.value === "platform_knowledge:read" ? "Read only" : "Read + write (curate)"}</option>
              ))}
            </select>
          </label>
          <button className="btn btnPrimary" disabled={busy || !name.trim()} onClick={() => void mint()}>{busy ? "Minting…" : "Mint key"}</button>
        </div>
        {minted && (
          <div className="card" style={{ borderColor: "var(--accent)", display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 600 }}>Key "{minted.name}" — copy it now, it will not be shown again.</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <code className="mono" style={{ userSelect: "all", wordBreak: "break-all", flex: 1 }}>{minted.token}</code>
              <button className="btn" onClick={() => { void navigator.clipboard.writeText(minted.token); toast.push({ kind: "success", message: "Copied." }); }}>Copy</button>
            </div>
            <div className="help">Add it to your AI tool as a Bearer token for the MCP endpoint: <code className="mono">{mcpUrl}</code></div>
            <div><button className="btn" onClick={() => setMinted(null)}>Done</button></div>
          </div>
        )}
      </div>
      <div className="tableWrap">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Prefix</th><th>Scope</th><th>Created</th><th>Last used</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {keys.length === 0 && <tr><td colSpan={7} className="muted" style={{ textAlign: "center", padding: 24 }}>No keys yet.</td></tr>}
            {keys.map((k) => (
              <tr key={k.id}>
                <td style={{ fontSize: 13 }}>{k.name}</td>
                <td className="mono" style={{ fontSize: 12 }}>{k.token_prefix}…</td>
                <td className="muted" style={{ fontSize: 12 }}>{k.scope === "platform_knowledge:read" ? "read" : "read + write"}</td>
                <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtDateTime(k.created_at)}</td>
                <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtDateTime(k.last_used_at)}</td>
                <td>{k.revoked_at ? <span className="pill pillBad">revoked</span> : <span className="pill pillOk">active</span>}</td>
                <td className="actions">
                  {!k.revoked_at && <button className="adminListAction isDanger" onClick={() => void revoke(k)}>Revoke</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Entry editor (separate route)
// ─────────────────────────────────────────────

export function AdminPlatformKnowledgeEditor() {
  const { name: routeName } = useParams<{ name: string }>();
  const isNew = routeName === undefined;
  const toast = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [existing, setExisting] = useState<Entry | null>(null);
  const [candidates, setCandidates] = useState<Entry[]>([]);
  const [gateBlock, setGateBlock] = useState<GateCase[] | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  // ADR-0201: a refused save renders in place — a toast cannot be acted on.
  const [checkBlock, setCheckBlock] = useState<CurationCheck | null>(null);
  const [fCadence, setFCadence] = useState("");
  const [cadenceMsg, setCadenceMsg] = useState<string | null>(null);
  const [entryVerification, setEntryVerification] = useState<VerificationFields | null>(null);
  // Named for its subject: this page already has a `verification` for the
  // ADR-0151 enforcement tier, which answers a different question.
  const [curationCheck, setCurationCheck] = useState<CurationCheck | null>(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [relatedOffer, setRelatedOffer] = useState<string[]>([]);
  // ADR-0150 §4.2 — serving-gate surfaces. The refusal card doubles for draft
  // autosave refusals (autosave pauses while it shows); the pressure card is
  // idempotent across saves (set from the latest response, cleared when absent).
  const [servingRefusal, setServingRefusal] = useState<{ message: string; detail: ServingRefusalDetail | null } | null>(null);
  const [pressure, setPressure] = useState<ServingSummary | null>(null);
  const [servingFieldError, setServingFieldError] = useState<string | null>(null);
  // ADR-0151 §4.2: computed verification (read-only; stamped by curation sessions).
  const [verification, setVerification] = useState<Entry["verification"] | null>(null);

  const [fName, setFName] = useState("");
  const [fAgent, setFAgent] = useState("query");
  const [fCategory, setFCategory] = useState("design");
  const [fPriority, setFPriority] = useState(70);
  const [fTags, setFTags] = useState<string[]>([]);
  const [fStatus, setFStatus] = useState("accepted");
  const [fDescription, setFDescription] = useState("");
  const [fRelated, setFRelated] = useState<string[]>([]);
  const [fBody, setFBody] = useState("");
  // "" = not chosen (new entries must choose; unclassified existing entries may stay unset — carry-forward).
  const [fServing, setFServing] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        const [m, list] = await Promise.all([
          apiFetch<Meta>("/api/admin/platform-knowledge-meta"),
          apiFetch<{ entries: Entry[] }>("/api/admin/platform-knowledge"),
        ]);
        setMeta(m);
        setCandidates(list.entries.filter((e) => e.name !== routeName));
        // ADR-0151 §4.2: verification rides the list response; read-only here.
        const mine = routeName ? list.entries.find((e) => e.name === routeName) : undefined;
        if (mine?.verification) setVerification(mine.verification);
      } catch {
        /* pickers degrade */
      }
      if (isNew) return;
      try {
        const res = await apiFetch<{ entry: Entry }>(`/api/admin/platform-knowledge/${routeName}`);
        const e = res.entry;
        setExisting(e);
        setFName(e.name); setFAgent(e.agent_type); setFCategory(e.category); setFPriority(e.priority);
        setFTags(e.scope_tags); setFStatus(e.status); setFDescription(e.description);
        setFRelated(e.related); setFBody(e.body ?? "");
        setFServing(e.serving_class ?? "");
        setFCadence(e.verify_every_days ? String(e.verify_every_days) : "");
        setEntryVerification({ verified_at: e.verified_at, verified_by_actor: e.verified_by_actor, lapsed: e.lapsed });
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [routeName, isNew]);

  const nameValid = SLUG_RE.test(fName.trim());
  const descValid = fDescription.trim().length > 0 && fDescription.trim().length <= 200;
  // Canon = writing accepted, OR editing an entry that is currently live+accepted.
  const isCanon = fStatus === "accepted" || (existing?.status === "accepted" && existing?.enabled !== false);
  const scopeTagOptions = useMemo(
    () => [...(meta?.scope_tags.functional ?? []), ...(meta?.scope_tags.capability ?? [])],
    [meta]
  );

  function bodyPayload(overrideOverride?: string, servingOverride?: string) {
    const serving = servingOverride ?? fServing;
    return {
      name: fName.trim(),
      agent_type: fAgent,
      category: fCategory,
      priority: fPriority,
      description: fDescription.trim(),
      body: fBody,
      scope_tags: fTags,
      status: fStatus,
      related: fRelated,
      // Omitted when unchosen: the seam requires it on new entries (inline
      // error) and carries the stored class forward on edits.
      ...(serving ? { serving_class: serving } : {}),
      ...(cadencePayload(fCadence) !== undefined ? { verify_every_days: cadencePayload(fCadence) } : {}),
      ...(overrideOverride ? { override_reason: overrideOverride } : {}),
    };
  }

  type SaveResponse = { entry: Entry; check: CurationCheck | null; serving?: ServingSummary };

  /** Accept the related-entry offer: one click, one save, no modal. */
  async function addRelated(names: string[]) {
    const next = [...fRelated, ...names.filter((n) => !fRelated.includes(n))];
    setFRelated(next);
    setRelatedOffer([]);
    try {
      await apiFetch<SaveResponse>("/api/admin/platform-knowledge", {
        method: "POST",
        body: { ...bodyPayload(), related: next },
      });
      toast.push({ kind: "success", message: "Related entries added." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not add related entries" });
    }
  }

  /** ADR-0201 §3.5 — a fresh check against the platform tree as it is now. */
  async function loadCurationCheck() {
    if (!routeName) return;
    setVerifyBusy(true);
    try {
      const res = await apiFetch<{ check: CurationCheck }>(`/api/admin/platform-knowledge/${routeName}/verification`);
      setCurationCheck(res.check);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not load verification" });
    } finally {
      setVerifyBusy(false);
    }
  }

  /** "Still right" — restamp, change nothing else. */
  async function markVerified() {
    if (!routeName) return;
    setVerifyBusy(true);
    try {
      const res = await apiFetch<{ entry: Entry }>(`/api/admin/platform-knowledge/${routeName}/verify`, { method: "POST" });
      setEntryVerification({
        verified_at: res.entry.verified_at,
        verified_by_actor: res.entry.verified_by_actor,
        lapsed: res.entry.lapsed,
      });
      toast.push({ kind: "success", message: "Verified." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not verify" });
    } finally {
      setVerifyBusy(false);
    }
  }

  function applyServingSummary(s: ServingSummary | undefined) {
    // Idempotent: the latest response wins; no pressure → card clears.
    setPressure(s && s.pressure.length > 0 ? s : null);
  }

  function handleServingRefusal(e: ApiRequestError): void {
    const data = e.data as { detail?: ServingRefusalDetail; message?: string } | undefined;
    setServingRefusal({ message: e.message, detail: data?.detail ?? null });
  }

  // Draft autosave: silent, debounced, only while the entry is a draft (no eval
  // gate — but ADR-0150: drafts serve, so the SERVING gate applies; a refusal
  // renders the danger card and the autosave pauses on the refused content
  // rather than retrying in a loop or degrading to a generic error toast).
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (loading || isCanon || !nameValid || !descValid || servingRefusal) return;
    dirtyRef.current = true;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      void apiFetch<SaveResponse>("/api/admin/platform-knowledge", { method: "POST", body: bodyPayload() })
        .then((r) => { dirtyRef.current = false; applyServingSummary(r.serving); })
        .catch((e) => {
          if (e instanceof ApiRequestError && e.code === "serving_regression") {
            handleServingRefusal(e); // structured validation, never the silent-failure path
          }
          /* other draft autosave failures stay silent; surfaced on explicit save */
        });
    }, 900);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fAgent, fCategory, fPriority, fTags, fStatus, fDescription, fRelated, fBody, fServing, isCanon, nameValid, descValid, loading, servingRefusal]);

  async function saveCanon(override?: string, servingOverride?: string) {
    setBusy(true);
    setGateBlock(null);
    setCheckBlock(null);
    setServingRefusal(null);
    setServingFieldError(null);
    try {
      const r = await apiFetch<SaveResponse>("/api/admin/platform-knowledge", {
        method: "POST",
        body: bodyPayload(override, servingOverride),
      });
      toast.push({ kind: "success", message: "Saved." });
      // ADR-0201 §8.2 — a `complements` finding is an offer, not an auto-add.
      const offer = (r.check?.findings ?? [])
        .filter((f) => f.relation === "complements" && !fRelated.includes(f.name))
        .map((f) => f.name);
      if (offer.length > 0) setRelatedOffer(offer);
      if (r.serving && r.serving.pressure.length > 0) {
        // §4.2: stay on the page so the envelope-pressure card is seen.
        applyServingSummary(r.serving);
      } else if (offer.length === 0) {
        navigate("/admin/platform-knowledge");
      }
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "curation_check") {
        const check = (e.data as { check?: CurationCheck } | undefined)?.check;
        if (check) {
          setCheckBlock(check);
          return;
        }
        toast.push({ kind: "error", message: e.message });
      } else if (e instanceof ApiRequestError && e.code === "eval_regression") {
        const detail = (e.data as { detail?: { failing_cases?: GateCase[] } } | undefined)?.detail;
        setGateBlock(detail?.failing_cases ?? []);
      } else if (e instanceof ApiRequestError && e.code === "serving_regression") {
        handleServingRefusal(e);
      } else if (e instanceof ApiRequestError && e.code === "invalid_serving_class") {
        // §4.1: inline validation under the Serving field, never a toast.
        setServingFieldError(e.message);
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : "Save failed" });
      }
    } finally {
      setBusy(false);
    }
  }

  // §4.2 remedy: reclassify to on-demand and re-save in one gesture. For a
  // refused draft autosave, clearing the refusal resumes the paused autosave
  // with the new class (the effect keys on fServing).
  function reclassifyOnDemandAndSave() {
    setFServing("on_demand");
    setServingRefusal(null);
    if (isCanon) void saveCanon(undefined, "on_demand");
  }

  async function archive() {
    if (!routeName) return;
    const ok = await confirm({ title: "Archive entry?", detail: `Archive "${routeName}"? It leaves the index and stops loading into agents.`, confirmLabel: "Archive" });
    if (!ok) return;
    try {
      await apiFetch(`/api/admin/platform-knowledge/${routeName}/archive`, { method: "POST", body: { reason: "archived from console" } });
      toast.push({ kind: "success", message: "Archived." });
      navigate("/admin/platform-knowledge");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Archive failed" });
    }
  }

  async function toggleEnabled() {
    if (!routeName || !existing) return;
    try {
      await apiFetch(`/api/admin/platform-knowledge/${routeName}/enabled`, { method: "POST", body: { enabled: existing.enabled === false } });
      toast.push({ kind: "success", message: existing.enabled === false ? "Enabled." : "Disabled." });
      navigate("/admin/platform-knowledge");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Failed" });
    }
  }

  const title = (
    <>
      <Link to="/admin/platform-knowledge">Platform Knowledge</Link>
      {" / "}
      {isNew ? "New entry" : routeName}
    </>
  );

  if (error) return <PageLayout title={title}><ErrorState detail={error} /></PageLayout>;
  if (loading) return <PageLayout title={title}><LoadingState /></PageLayout>;

  return (
    <PageLayout
      title={title}
      actions={
        <>
          <button className="btn" disabled={busy} onClick={() => navigate("/admin/platform-knowledge")}>Back</button>
          {!isNew && existing && (
            <button className="btn" disabled={busy} onClick={() => void toggleEnabled()}>{existing.enabled === false ? "Enable" : "Disable"}</button>
          )}
          {!isNew && (
            <button className="btn btnDanger" disabled={busy} onClick={() => void archive()}>Archive</button>
          )}
          {isCanon ? (
            <button className="btn btnPrimary" disabled={busy || !nameValid || !descValid} onClick={() => void saveCanon()}>
              {busy ? "Validating…" : "Validate & save"}
            </button>
          ) : (
            <span className="pill pillWarn" title="Drafts autosave; no eval gate.">draft · autosaving</span>
          )}
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {isCanon && (
          <div className="help">
            Accepted entries load into every tenant's agents. Saving runs the eval gate — a write that regresses a previously-passing
            case is refused. Switch Status to <strong>draft</strong> to iterate without the gate (drafts autosave).
          </div>
        )}
        {relatedOffer.length > 0 && (
          <div className="card curationRelatedLine">
            <span>Related to {relatedOffer.join(", ")}.</span>
            <button className="btn btnSm" onClick={() => void addRelated(relatedOffer)}>Add</button>
            <button className="adminListAction" onClick={() => { setRelatedOffer([]); navigate("/admin/platform-knowledge"); }}>
              Not now
            </button>
          </div>
        )}
        {checkBlock && (
          <CurationCheckCard
            check={checkBlock}
            surface="platform"
            overrideReason={overrideReason}
            onOverrideReasonChange={setOverrideReason}
            onCancel={() => setCheckBlock(null)}
            onOverride={() => void saveCanon(overrideReason.trim())}
            busy={busy}
          />
        )}
        {gateBlock && (
          <div className="card" style={{ borderColor: "var(--danger-border)", background: "var(--danger-bg)", display: "grid", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Eval gate blocked this save — {gateBlock.length} case{gateBlock.length === 1 ? "" : "s"} regressed:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {gateBlock.map((c) => <li key={c.id} style={{ fontSize: 13 }}>{c.title}{c.error ? ` — ${c.error}` : ""}</li>)}
            </ul>
            <label className="field">
              <span className="label">Override reason (saves anyway, recorded to the audit)</span>
              <input className="input" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Why this is safe to override" />
            </label>
            <div className="actions">
              <button className="btn" onClick={() => setGateBlock(null)}>Cancel</button>
              <button className="btn btnDanger" disabled={busy || !overrideReason.trim()} onClick={() => void saveCanon(overrideReason.trim())}>Override & save</button>
            </div>
          </div>
        )}
        {servingRefusal && (
          // ADR-0150 §4.2 — serving refusal. Deliberately NO override control:
          // an override would mint a silent drop; every refusal has a remedy.
          <div className="card" style={{ borderColor: "var(--danger-border)", background: "var(--danger-bg)", display: "grid", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>This change would drop guidance from live prompts</div>
            {servingRefusal.detail ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {servingRefusal.detail.surfaces.map((s) => (
                  <li key={`${s.surface_id}:${s.agent_type}`} style={{ fontSize: 13 }}>
                    {s.label} would exceed its serving budget by {s.overflow_chars.toLocaleString()} characters, dropping{" "}
                    {s.dropped.length} {s.dropped.length === 1 ? "entry" : "entries"}:{" "}
                    {s.dropped.map((d, i) => (
                      <span key={d.name}>
                        {i > 0 && ", "}
                        <span title={d.description}>«{d.name}»</span>
                      </span>
                    ))}
                    {s.context_dependent ? " (base load; per-request additions are outside static reach)" : ""}.
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ fontSize: 13 }}>{servingRefusal.message}</div>
            )}
            <div className="help">
              The remedy is making the guidance fit, not forcing it through: tighten this entry's body, or reclassify
              lower-value entries so the resident set fits its budget.
            </div>
            <div className="actions">
              <button className="btn" onClick={() => setServingRefusal(null)}>Cancel</button>
              {servingRefusal.detail?.agent_on_demand_capable && (
                <button className="btn btnPrimary" disabled={busy} onClick={() => reclassifyOnDemandAndSave()}>
                  Serve this entry on demand
                </button>
              )}
            </div>
          </div>
        )}
        {pressure && !servingRefusal && (
          // §4.2 — envelope pressure: non-blocking, idempotent across autosaves.
          <div className="card" style={{ borderColor: "var(--warning-border, #fde68a)", display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 600 }}>Serving budget pressure</div>
            {pressure.pressure.map((p) => (
              <div key={`${p.surface_id}:${p.agent_type}`} style={{ fontSize: 13 }}>
                {p.agent_type.replace(/_/g, " ")}
                {p.surface_id !== p.agent_type ? ` (${p.surface_id.replace(/_/g, " ")})` : ""} is at{" "}
                {Math.round(p.utilization * 100)}% of its serving budget ({p.headroom_chars.toLocaleString()} characters of headroom).
              </div>
            ))}
            {pressure.reclassify_suggestion && <div className="help">{pressure.reclassify_suggestion}</div>}
          </div>
        )}
        <div className="knowledgeEditorLayout">
          <div className="card" style={{ display: "grid", gap: 10, alignSelf: "start", alignContent: "start" }}>
            <label className="field">
              <span className="label">Name</span>
              <input className="input mono" maxLength={64} value={fName} disabled={!isNew} onChange={(e) => setFName(e.target.value)} placeholder="kebab-case-slug" />
              <div className="help">{isNew ? "Lowercase letters, digits, hyphens (3–64). Permanent." : "Names are permanent — to rename, archive and recreate."}</div>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <label className="field">
                <span className="label">Agent</span>
                <select className="select" value={fAgent} onChange={(e) => setFAgent(e.target.value)}>
                  {(meta?.agent_types ?? [fAgent]).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </label>
              <label className="field">
                <span className="label">Category</span>
                <select className="select" value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
                  {(meta?.categories ?? [fCategory]).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <label className="field">
                <span className="label">Priority</span>
                <input className="input" type="number" min={1} max={999} value={fPriority} onChange={(e) => setFPriority(Number(e.target.value))} />
                <div className="help">0–49 foundational · 50–99 ranked · 100+ reference</div>
              </label>
              <label className="field">
                <span className="label">Status</span>
                <select className="select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                  <option value="accepted">accepted</option>
                  <option value="draft">draft</option>
                </select>
              </label>
              <label className="field">
                <span className="label">Serving</span>
                <select
                  className="select"
                  value={fServing}
                  onChange={(e) => { setFServing(e.target.value); setServingFieldError(null); }}
                >
                  <option value="" disabled={!!existing?.serving_class}>Choose…</option>
                  <option value="always">Always loaded</option>
                  <option value="conditional">Conditional (scope tags)</option>
                  <option value="on_demand">On demand</option>
                </select>
                {servingFieldError ? (
                  <div className="help" style={{ color: "var(--danger-text)" }}>{servingFieldError}</div>
                ) : (
                  <div className="help">
                    {fServing
                      ? SERVING_CLASS_HELP[fServing]
                      : isNew
                        ? "How this entry reaches its agent — choose before saving."
                        : "Unclassified — serves as Always until classified."}
                  </div>
                )}
              </label>
            </div>
            <label className="field">
              <span className="label">Verify every (days)</span>
              <input
                className="input"
                type="number"
                min={1}
                max={365}
                value={fCadence}
                onChange={(e) => setFCadence(e.target.value)}
                onBlur={() => setCadenceMsg(cadenceError(fCadence))}
                placeholder="90"
              />
              <div className="help">{cadenceMsg ?? VERIFY_HELP.cadenceField}</div>
            </label>
            <label className="field">
              <span className="label">Description</span>
              <textarea className="input" rows={2} maxLength={200} style={{ resize: "vertical" }} value={fDescription} onChange={(e) => setFDescription(e.target.value)} placeholder="One line — the hook shown in the index (≤200)" />
            </label>
            <div className="field">
              <span className="label">Scope tags</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                {fTags.map((t) => (
                  <span key={t} className="pill pillOk" style={{ fontSize: 11, cursor: "pointer" }} title="Remove" onClick={() => setFTags(fTags.filter((x) => x !== t))}>{t} ×</span>
                ))}
                <select className="select" style={{ fontSize: 12 }} value="" onChange={(e) => { if (e.target.value && !fTags.includes(e.target.value)) setFTags([...fTags, e.target.value]); e.target.value = ""; }}>
                  <option value="">+ add</option>
                  {scopeTagOptions.filter((t) => !fTags.includes(t)).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="help">Empty = loads everywhere. Otherwise a functional tag or a node-type capability tag.</div>
            </div>
            <div className="field">
              <span className="label">Related entries</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                {fRelated.map((r) => (
                  <span key={r} className="pill" style={{ fontSize: 11, cursor: "pointer" }} title="Remove" onClick={() => setFRelated(fRelated.filter((x) => x !== r))}>{r} ×</span>
                ))}
                <select className="select" style={{ fontSize: 12 }} value="" onChange={(e) => { if (e.target.value && !fRelated.includes(e.target.value)) setFRelated([...fRelated, e.target.value]); e.target.value = ""; }}>
                  <option value="">+ add</option>
                  {candidates.filter((c) => !fRelated.includes(c.name)).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>
            </div>
            {existing && verification && (
              <div className="field">
                <span className="label">Verification</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {verificationPill(verification)}
                  <span className="muted" style={{ fontSize: 12 }}>
                    {verification.broken
                      ? "A cited automatic check is disabled or missing — fix the check or remove the claim."
                      : VERIFICATION_HELP[verification.tier]}
                    {verification.eval_cases > 0 ? ` ${verification.eval_cases} linked eval case${verification.eval_cases === 1 ? "" : "s"}.` : ""}
                  </span>
                </div>
              </div>
            )}
            {existing && (
              <div className="muted" style={{ fontSize: 12 }}>
                Updated {fmtDateTime(existing.updated_at)} by {existing.updated_by.replace(/^(account|key|capture|escalation|system):/, "")}
              </div>
            )}
            {existing?.usage && (
              <div className="muted" style={{ fontSize: 12 }}>{usageProse(existing.usage)}</div>
            )}
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <span className="label">Body</span>
              <span className="muted" style={{ fontSize: 12 }}>Markdown — SQL in ```sql fences. Preview on the right.</span>
            </div>
            <div className="knowledgeEditorSplit">
              <textarea className="input" value={fBody} onChange={(e) => setFBody(e.target.value)} placeholder={"State the rule or technique…\n\n```sql\nSELECT ...\n```"} />
              <div className="knowledgeMarkdownPane"><PlatformMarkdown body={fBody || "*Nothing to preview yet.*"} /></div>
            </div>
          </div>
        </div>

        {!isNew && (
          <div className="card" style={{ display: "grid", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Verification</div>
            <div className="help">{VERIFY_HELP.column}</div>
            <div style={{ fontSize: 13 }}>
              {entryVerification ? verifiedLine(entryVerification) : "Never verified"}
              {entryVerification?.lapsed ? <> &middot; <span className="pill pillWarn">lapsed</span></> : null}
            </div>
            {curationCheck ? (
              <CurationVerdictPanel check={curationCheck} surface="platform" />
            ) : (
              <div className="help">Check this entry against the platform guidance as it is now before confirming it.</div>
            )}
            <div className="actions">
              {!curationCheck && (
                <button className="btn btnSm" disabled={verifyBusy} onClick={() => void loadCurationCheck()}>
                  {verifyBusy ? "Checking…" : "Check against current guidance"}
                </button>
              )}
              <button className="btn btnSm" disabled={verifyBusy} onClick={() => void markVerified()}>Still right</button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
